import React, { useState, useEffect } from 'react';
import { fetchBookings, fetchProfessionals, createPayment, fetchPayments, updatePayment, createDailyCaixa, createPaymentReport } from '../api';
import './Financeiro.css';
import PaymentModal from './Modals/PaymentModal';
import CommissionModal from './Modals/CommissionModal';
import PaymentInfoModal from './Modals/PaymentInfoModal';
import ViewBookingModal from './Modals/ViewBookingModal';
import { ComissoesTab, PagamentosTab } from './Tabs';
import { useNavigate } from 'react-router-dom';

function Financeiro() {
  const [activeTab, setActiveTab] = useState('comissoes');
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [professionals, setProfessionals] = useState([]);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [showModal, setShowModal] = useState(false);
  const [comissionStatus, setComissionStatus] = useState('unpaid');
  const [selectedProfessionals, setSelectedProfessionals] = useState([]);
  const [selectedProcedure, setSelectedProcedure] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('sent');
  const [paymentStartDate, setPaymentStartDate] = useState('');
  const [paymentEndDate, setPaymentEndDate] = useState('');
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [invoicesError, setError] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentInfoModal, setShowPaymentInfoModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfessionals()
      .then(data => {
        const professionalsList = Object.entries(data.config.profissionais).map(([name, details]) => ({
          name: name,
          ...details
        }));
        setProfessionals(professionalsList);
      })
      .catch(error => console.error('Error fetching professionals:', error));

    // Load today's bookings with proper date formatting
    const today = new Date().toISOString().split('T')[0];
    const queryParams = {
      startDate: `${today}`,
      endDate: `${today}`,
      comissionStatus: comissionStatus
    };
    loadBookings(queryParams);

    // Set default dates to current week's Monday and Friday
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - now.getDay() + 1);
    const friday = new Date(now);
    friday.setDate(now.getDate() - now.getDay() + 5);
    
    setPaymentStartDate(monday.toISOString().split('T')[0]);
    setPaymentEndDate(friday.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (activeTab === 'pagamentos') {
      loadPayments();
    }
  }, [activeTab, paymentStatus, paymentStartDate, paymentEndDate]);

  const loadBookings = async (queryParams = {}) => {
    try {
      setBookingsLoading(true);
      
      const formattedParams = {
        ...queryParams
      };

      if (queryParams.startDate) {
        formattedParams.startDate = `${queryParams.startDate}`;
        formattedParams.endDate = `${queryParams.endDate}`;
      }

      if (comissionStatus) {
        formattedParams.comissionStatus = comissionStatus;
      }

      const bookingsData = await fetchBookings(formattedParams);
      setBookings(bookingsData);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setBookingsLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      setPaymentsLoading(true);
      const filters = {
        status: paymentStatus,
        start_date: `${paymentStartDate}T12:00:00Z`,
        end_date: `${paymentEndDate}T12:00:00Z`
      };
      const data = await fetchPayments(filters);
      setPayments(data);
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleSearch = () => {
    if (!startDate || !endDate) {
      alert('Por favor, selecione as datas de início e fim');
      return;
    }

    const queryParams = {
      startDate: `${startDate}`,
      endDate: `${endDate}`
    };

    loadBookings(queryParams);
  };

  const calculateTotalValue = (procedures) => {
    return procedures.reduce((total, proc) => total + proc.valor, 0);
  };

  const groupBookingsByProfessional = () => {
    const groupedBookings = {};
    const filteredBookings = getFilteredBookings();

    filteredBookings
      .filter(booking => booking.status === 'Agendado' || booking.status === 'Realizado')
      .forEach(booking => {
        const profId = booking.profissional;
        if (!groupedBookings[profId]) {
          groupedBookings[profId] = {
            bookings: [],
            total: 0,
            totalComission: 0
          };
        }
        const bookingTotal = calculateTotalValue(booking.procedimentos);
        groupedBookings[profId].bookings.push(booking);
        groupedBookings[profId].total += bookingTotal;
        groupedBookings[profId].totalComission += parseFloat(booking.comission || 0);
      });
    
    return groupedBookings;
  };

  const getProfessionalName = (professionalId) => {
    const professional = professionals.find(p => p.id === professionalId);
    return professional ? professional.name : professionalId;
  };

  const getBaseProcedureName = (procedureName) => {
    return procedureName.split('-')[0].trim();
  };

  const getUniqueProcedures = () => {
    const baseNames = new Set();
    bookings.forEach(booking => {
      booking.procedimentos?.forEach(proc => {
        const baseName = getBaseProcedureName(proc.nome);
        if (baseName) baseNames.add(baseName);
      });
    });
    return Array.from(baseNames).sort();
  };

  const getFilteredBookings = () => {
    let filtered = bookings;
    
    if (selectedProfessionals.length > 0) {
      filtered = filtered.filter(booking => 
        selectedProfessionals.includes(booking.profissional)
      );
    }
    
    if (selectedProcedure) {
      filtered = filtered.filter(booking =>
        booking.procedimentos?.some(proc =>
          getBaseProcedureName(proc.nome) === selectedProcedure
        )
      );
    }
    
    return filtered;
  };

  const handleRowClick = (booking) => {
    setSelectedBooking(booking);
    setShowModal(true);
  };

  const handleQuickFilter = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    
    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];
    
    setStartDate(startStr);
    setEndDate(endStr);
    
    loadBookings({
      startDate: `${startStr}`,
      endDate: `${endStr}`
    });
  };

  const handleCreatePayment = async (paymentData) => {
    try {
      await createPayment(paymentData);
      loadPayments(); // Refresh the payments list
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (window.confirm('Quer mesmo deletar o pagamento?')) {
      try {
        await updatePayment(paymentId, {}, true);
        // Refresh payments list
        loadPayments();
      } catch (err) {
        setError('Failed to delete payment');
        console.error(err);
      }
    }
  };

  const handleGeneratePDF = async (includePayments) => {
    try {
      const result = await createDailyCaixa({
        startDate: `${startDate}`,
        endDate: `${endDate}`,
        professionalIds: selectedProfessionals.length > 0 ? selectedProfessionals : undefined,
        comissionStatus,
        include_payments: includePayments === true ? true : undefined,
        procedureFilter: selectedProcedure || undefined
      });
      if (result && result.url) {
        window.open(result.url, '_blank', 'noopener,noreferrer');
      } else {
        alert('Falha ao gerar o relatório.');
      }
    } catch (err) {
      console.error('Erro ao gerar relatório diário:', err);
      alert('Erro ao gerar relatório. Tente novamente.');
    }
  };

  const handleGeneratePaymentsReport = async (includeFiles) => {
    try {
      const result = await createPaymentReport({
        startDate: paymentStartDate,
        endDate: paymentEndDate,
        status: paymentStatus,
        includeFiles
      });
      return result && result.url ? result.url : null;
    } catch (err) {
      console.error('Erro ao gerar relatório de pagamentos:', err);
      return null;
    }
  };

  return (
    <div className="financeiro-container">
      <div className="financeiro-tabs">
        <button 
          className={`tab-button ${activeTab === 'comissoes' ? 'active' : ''}`}
          onClick={() => setActiveTab('comissoes')}
        >
          Comissões
        </button>
        <button 
          className={`tab-button ${activeTab === 'pagamentos' ? 'active' : ''}`}
          onClick={() => setActiveTab('pagamentos')}
        >
          Pagamentos
        </button>
      </div>

      {activeTab === 'comissoes' && (
        <ComissoesTab
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          comissionStatus={comissionStatus}
          setComissionStatus={setComissionStatus}
          professionals={professionals}
          selectedProfessionals={selectedProfessionals}
          setSelectedProfessionals={setSelectedProfessionals}
          selectedProcedure={selectedProcedure}
          setSelectedProcedure={setSelectedProcedure}
          uniqueProcedures={getUniqueProcedures()}
          bookingsLoading={bookingsLoading}
          bookings={bookings}
          handleSearch={handleSearch}
          handleQuickFilter={handleQuickFilter}
          groupBookingsByProfessional={groupBookingsByProfessional}
          getProfessionalName={getProfessionalName}
          handleRowClick={handleRowClick}
          handleGeneratePDF={handleGeneratePDF}
          setBookings={setBookings}
        />
      )}

      {activeTab === 'pagamentos' && (
        <PagamentosTab
          paymentStartDate={paymentStartDate}
          setPaymentStartDate={setPaymentStartDate}
          paymentEndDate={paymentEndDate}
          setPaymentEndDate={setPaymentEndDate}
          paymentStatus={paymentStatus}
          setPaymentStatus={setPaymentStatus}
          showPaymentModal={showPaymentModal}
          setShowPaymentModal={setShowPaymentModal}
          showCommissionModal={showCommissionModal}
          setShowCommissionModal={setShowCommissionModal}
          invoicesError={invoicesError}
          paymentsLoading={paymentsLoading}
          payments={payments}
          handleDeletePayment={handleDeletePayment}
          setSelectedPayment={setSelectedPayment}
          setShowPaymentInfoModal={setShowPaymentInfoModal}
          handleGeneratePaymentsReport={handleGeneratePaymentsReport}
        />
      )}

      <ViewBookingModal
        booking={selectedBooking}
        showModal={showModal}
        startDate={startDate}
        endDate={endDate}
        onUpdate={() => {
          const queryParams = {
            startDate: `${startDate}`,
            endDate: `${endDate}`
          };
          loadBookings(queryParams);
        }}
        onClose={() => {
          setShowModal(false);
          setSelectedBooking(null);
        }}
      />

      <PaymentModal
        show={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSubmit={handleCreatePayment}
      />

      <CommissionModal
        show={showCommissionModal}
        onClose={() => {
          setShowCommissionModal(false);
          loadPayments(); // Refresh payments after modal closes
        }}
      />

      <PaymentInfoModal
        show={showPaymentInfoModal}
        payment={selectedPayment}
        onClose={() => {
          setShowPaymentInfoModal(false);
          setSelectedPayment(null);
        }}
        onUpdate={() => loadPayments()}
      />
    </div>
  );
}

export default Financeiro; 